/**
 * Illustration style tokens (see docs/ART-BIBLE.md). Every figure is described once (regions,
 * ink lines, spots, construction guides) and painted by `Figure` with one of these styles.
 * All widths are in figure units at 1x = a 240-unit viewBox.
 */

import { ART_PALETTES, BASE_PALETTE, type ArtStyleId } from '@sketchling/core';

export type StyleId = ArtStyleId;

/** Colour roles a figure may use; each style maps them to its own palette. */
export type Role =
  | 'paper'
  | 'ground'
  | 'catFur'
  | 'catStripe'
  | 'cream'
  | 'pink'
  | 'eye'
  | 'collar'
  | 'bell'
  | 'foxFur'
  | 'foxDark'
  | 'foxTip'
  | 'scarf'
  | 'skin'
  | 'hair'
  | 'sweater'
  | 'overall'
  | 'glasses'
  | 'mouth'
  | 'pencil'
  | 'pencilWood'
  | 'eraser'
  | 'blush'
  | 'white'
  // world (Animals: forest & meadow)
  | 'sky'
  | 'skyLow'
  | 'cloud'
  | 'sun'
  | 'hillFar'
  | 'hillMid'
  | 'meadow'
  | 'meadowLight'
  | 'treeDark'
  | 'tree'
  | 'trunk'
  | 'pine'
  | 'pond'
  | 'pondLight'
  | 'trail'
  | 'flowerA'
  | 'flowerB'
  | 'flowerC';

export interface ArtStyle {
  id: StyleId;
  name: { en: string; vi: string };
  tagline: { en: string; vi: string };
  /** Ink colour (never pure black). */
  line: string;
  /** 0..1: how much each region's outline is tinted toward its own fill (0 = pure ink). */
  lineTint: number;
  /** Visible outer-contour width (the silhouette). */
  outer: number;
  /** Inner boundaries between regions. */
  inner: number;
  /** Max width of tapered detail strokes (whiskers, brows, mouths). */
  detail: number;
  /** Construction-guide colour + width. */
  guide: { color: string; width: number };
  /** Shade: fill mixed toward `color` by `amount`, painted as an offset crescent (light from top-left). */
  shade: { color: string; amount: number; offset: number };
  /** Rim highlight on the lit side (0 = off). */
  rim: { color: string; opacity: number; offset: number };
  /** Paper grain over fills (0 = off). */
  grain: number;
  /** White sticker border around the silhouette (0 = off) + hard drop shadow. */
  sticker: { width: number; shadow: string };
  /** Blush opacity multiplier. */
  blush: number;
  palette: Record<string, string>;
  /** UI chrome around the art (cards, nodes, map frame). */
  ui: {
    font: string;
    bg: string;
    surface: string;
    text: string;
    muted: string;
    accent: string;
    accentInk: string;
    radius: number;
    cardBorder: string;
    cardShadow: string;
    nodeRing: string;
  };
}

const animalsWorldA = {
  sky: '#CFE8E4',
  skyLow: '#F3EAD0',
  cloud: '#FFF9EE',
  sun: '#F6C453',
  hillFar: '#A9CDB4',
  hillMid: '#8DBE8A',
  meadow: '#9CCB72',
  meadowLight: '#B8DB8A',
  treeDark: '#4E8A5C',
  tree: '#6FAA62',
  trunk: '#8A5A3C',
  pine: '#3F7A5A',
  pond: '#7CC3CF',
  pondLight: '#B6E1E4',
  trail: '#EBD3A4',
  flowerA: '#F27E7E',
  flowerB: '#F7D35C',
  flowerC: '#FFFFFF',
};

export const STYLES: Record<StyleId, ArtStyle> = {
  A: {
    id: 'A',
    name: { en: 'Storybook', vi: 'Sách tranh' },
    tagline: { en: 'Warm, textured flat colour with a soft shade, like a picture book.', vi: 'Màu phẳng ấm, có vân giấy và bóng mềm, như sách tranh thiếu nhi.' },
    line: '#4A3226',
    lineTint: 0.28,
    outer: 3.6,
    inner: 2.2,
    detail: 2.6,
    guide: { color: '#7DBDE8', width: 1.6 },
    shade: { color: '#6D3D6B', amount: 0.24, offset: 9 },
    rim: { color: '#FFFFFF', opacity: 0, offset: 3 },
    grain: 0.16,
    sticker: { width: 0, shadow: 'none' },
    blush: 0.55,
    palette: {
      ...BASE_PALETTE.A,
      ...animalsWorldA,
    },
    ui: {
      font: "'Fredoka Variable', 'Fredoka', system-ui, sans-serif",
      bg: '#FBF1E1',
      surface: '#FFFBF3',
      text: '#4A3226',
      muted: '#8C7262',
      accent: '#E9783A',
      accentInk: '#FFFBF3',
      radius: 20,
      cardBorder: '1.5px solid #EAD8BF',
      cardShadow: '0 6px 0 #EAD8BF, 0 10px 24px rgba(74,50,38,.12)',
      nodeRing: '#E9783A',
    },
  },
  B: {
    id: 'B',
    name: { en: 'Sticker pop', vi: 'Sticker rực rỡ' },
    tagline: { en: 'Bold variable outline, saturated flat colours and a white sticker border.', vi: 'Viền đậm thay đổi độ dày, màu phẳng rực rỡ, viền trắng như sticker.' },
    line: '#2A1E3F',
    lineTint: 0,
    outer: 5.2,
    inner: 2.8,
    detail: 3.4,
    guide: { color: '#57B6F2', width: 1.8 },
    shade: { color: '#3B1E78', amount: 0.26, offset: 8 },
    rim: { color: '#FFFFFF', opacity: 0.55, offset: 3.5 },
    grain: 0,
    sticker: { width: 7, shadow: 'rgba(42,30,63,.28)' },
    blush: 0.7,
    palette: {
      ...BASE_PALETTE.B,
      sky: '#8FD8FF',
      skyLow: '#C9F0FF',
      cloud: '#FFFFFF',
      sun: '#FFD12E',
      hillFar: '#6FD6A8',
      hillMid: '#3FC77E',
      meadow: '#7FDB4E',
      meadowLight: '#A6EC6C',
      treeDark: '#1E9E5A',
      tree: '#38C46A',
      trunk: '#9A5B34',
      pine: '#15885A',
      pond: '#3EB8F5',
      pondLight: '#9BE2FF',
      trail: '#FFE3A3',
      flowerA: '#FF4D6D',
      flowerB: '#FFD12E',
      flowerC: '#FFFFFF',
    },
    ui: {
      font: "'Fredoka Variable', 'Fredoka', system-ui, sans-serif",
      bg: '#EFEAFF',
      surface: '#FFFFFF',
      text: '#2A1E3F',
      muted: '#6E6390',
      accent: '#FF7A2E',
      accentInk: '#FFFFFF',
      radius: 24,
      cardBorder: '3px solid #2A1E3F',
      cardShadow: '0 5px 0 #2A1E3F',
      nodeRing: '#FF7A2E',
    },
  },
  C: {
    id: 'C',
    name: { en: 'Soft pastel kawaii', vi: 'Pastel dễ thương' },
    tagline: { en: 'Thin warm-brown line, pastel fills, rosy blush and sparkly eyes.', vi: 'Nét nâu ấm mảnh, màu pastel, má hồng và mắt long lanh.' },
    line: '#8E5E4E',
    lineTint: 0.12,
    outer: 2.2,
    inner: 1.5,
    detail: 1.9,
    guide: { color: '#8CCBF0', width: 1.4 },
    shade: { color: '#D77FA6', amount: 0.2, offset: 8 },
    rim: { color: '#FFFFFF', opacity: 0.6, offset: 3 },
    grain: 0,
    sticker: { width: 0, shadow: 'none' },
    blush: 1,
    palette: {
      ...BASE_PALETTE.C,
      sky: '#DDEFFB',
      skyLow: '#FCEFF4',
      cloud: '#FFFFFF',
      sun: '#FFE7A6',
      hillFar: '#D4ECD9',
      hillMid: '#C1E5C4',
      meadow: '#CDEBB7',
      meadowLight: '#E0F4CC',
      treeDark: '#9FD3B0',
      tree: '#B5E2B9',
      trunk: '#D7AE95',
      pine: '#93CBA9',
      pond: '#BFE3F2',
      pondLight: '#E4F5FB',
      trail: '#FBEBD6',
      flowerA: '#FFB0BF',
      flowerB: '#FFE08F',
      flowerC: '#FFFFFF',
    },
    ui: {
      font: "'Fredoka Variable', 'Fredoka', system-ui, sans-serif",
      bg: '#FFF4F6',
      surface: '#FFFFFF',
      text: '#7A4E44',
      muted: '#B08C86',
      accent: '#FF9DB2',
      accentInk: '#FFFFFF',
      radius: 28,
      cardBorder: '1.5px solid #F6DCE3',
      cardShadow: '0 8px 22px rgba(214,140,160,.22)',
      nodeRing: '#FF9DB2',
    },
  },
};

export const STYLE_IDS: StyleId[] = ['A', 'B', 'C'];

/** Every colour role (style tokens + all world figure roles from core) for one style. */
const cache = new Map<StyleId, Record<string, string>>();
export function stylePalette(id: StyleId): Record<string, string> {
  let p = cache.get(id);
  if (!p) {
    p = { ...STYLES[id].palette, ...ART_PALETTES[id] };
    cache.set(id, p);
  }
  return p;
}
